import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  getWeeklyLeaderboard, 
  getUserRank, 
  LeaderboardEntry, 
  UserRankInfo,
  getRankMedal,
  formatDisplayName,
  getLeagueEmoji,
  getLeagueColor
} from '../utils/leaderboardUtils';
import { supabase } from '../supabase';
import { isGuestUser } from '../utils/guestUtils';
import BottomNav from '../components/BottomNav';
import LessonBannerAd from '../components/LessonBannerAd';

const LeaderboardScreen: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'weekly' | 'alltime'>('weekly');
  const [weeklyLeaders, setWeeklyLeaders] = useState<LeaderboardEntry[]>([]);
  const [alltimeLeaders, setAlltimeLeaders] = useState<LeaderboardEntry[]>([]);
  const [userRank, setUserRank] = useState<UserRankInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentLanguage, setCurrentLanguage] = useState<'bn' | 'it' | 'en'>('bn');
  const [isGuest, setIsGuest] = useState<boolean>(false);

  const content = {
    bn: {
      title: "লিডারবোর্ড",
      weekly: "সাপ্তাহিক",
      alltime: "সর্বকালের সেরা",
      yourRank: "আপনার র‍্যাংক",
      rank: "র‍্যাংক",
      user: "ব্যবহারকারী",
      xp: "XP"
    },
    it: {
      title: "Classifica",
      weekly: "Settimanale",
      alltime: "Tutti i Tempi",
      yourRank: "Il Tuo Rango",
      rank: "Rango",
      user: "Utente",
      xp: "XP"
    },
    en: {
      title: "Leaderboard",
      weekly: "Weekly",
      alltime: "All Time",
      yourRank: "Your Rank",
      rank: "Rank",
      user: "User",
      xp: "XP"
    }
  };

  const lang = content[currentLanguage] || content.en;

  useEffect(() => {
    loadUser();
    setIsGuest(isGuestUser());
    const savedLanguage = localStorage.getItem('preferredLanguage') as 'bn' | 'it' | 'en';
    if (savedLanguage) {
      setCurrentLanguage(savedLanguage);
    }
  }, []);

  useEffect(() => {
    if (user && !isGuest) {
      loadLeaderboardData();
    } else if (isGuest) {
      setLoading(false);
    }
  }, [user, activeTab, isGuest]);

  const loadUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setUser(user);
  };

  const loadLeaderboardData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'weekly') {
        // Load weekly XP leaderboard
        const leaders = await getWeeklyLeaderboard(50);
        setWeeklyLeaders(leaders);
        
        // Load user's weekly rank
        if (user) {
          const rank = await getUserRank(user.id);
          setUserRank(rank);
        }
      } else if (activeTab === 'alltime') {
        // Load all-time total XP leaderboard
        const { data, error } = await supabase
          .from('users')
          .select('id, display_name, full_name, email, xp, streak')
          .order('xp', { ascending: false })
          .limit(50);
        
        if (!error && data) {
          setAlltimeLeaders(data.map((item: any, index) => ({
            rank: index + 1,
            user_id: item.id,
            display_name: item.display_name || item.full_name || item.email?.split('@')[0] || 'User',
            total_xp: item.xp || 0,
            lessons_completed: 0,
            streak: item.streak || 0,
            league: 'bronze'
          })));
          
          // Find user's all-time rank
          if (user) {
            const userIndex = data.findIndex((item: any) => item.id === user.id);
            if (userIndex >= 0) {
              setUserRank({
                user_rank: userIndex + 1,
                total_xp: data[userIndex].xp || 0,
                next_user_name: userIndex > 0 ? (data[userIndex - 1].display_name || data[userIndex - 1].full_name || 'User') : null,
                next_user_xp: userIndex > 0 ? (data[userIndex - 1].xp || 0) : null,
                xp_needed: userIndex > 0 ? ((data[userIndex - 1].xp || 0) - (data[userIndex].xp || 0)) : null,
                total_users: data.length
              });
            }
          }
        }
      }
    } catch (error) {
      console.error('Error loading leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="leaderboard-screen">
      {/* Header */}
      <div className="screen-header">
        <button 
          className="header-btn back-btn"
          onClick={() => navigate(-1)}
          aria-label="Go back"
        >
          ←
        </button>
        <div className="header-center">
          <h1>{lang.title}</h1>
        </div>
        <button 
          className="header-btn settings-btn"
          onClick={() => navigate('/settings')}
          aria-label="Open settings"
        >
          ⋯
        </button>
      </div>

      <LessonBannerAd />

      {/* Guest User Message */}
      {isGuest ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '60px 20px',
          textAlign: 'center',
          minHeight: '50vh'
        }}>
          <div style={{
            fontSize: '64px',
            marginBottom: '20px'
          }}>
            🏆
          </div>
          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: '700',
            color: 'white',
            marginBottom: '12px'
          }}>
            Leaderboard Available
          </h2>
          <p style={{
            fontSize: '1rem',
            color: 'white',
            lineHeight: '1.6',
            marginBottom: '30px',
            maxWidth: '400px'
          }}>
            Create an account to see your rank on the leaderboard and compete with other learners!
          </p>
          <button
            onClick={() => {
              // Clear guest flag before navigating
              localStorage.removeItem('isGuest');
              navigate('/register');
            }}
            style={{
              padding: '14px 28px',
              background: 'linear-gradient(135deg, #6B46C1 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(107, 70, 193, 0.3)',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(107, 70, 193, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(107, 70, 193, 0.3)';
            }}
          >
            Create Account
          </button>
          <button
            onClick={() => navigate('/login')}
            style={{
              marginTop: '12px',
              padding: '12px 24px',
              background: 'transparent',
              color: '#6B46C1',
              border: '2px solid #6B46C1',
              borderRadius: '12px',
              fontSize: '0.9rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
          >
            Sign In
          </button>
        </div>
      ) : (
        <>
      {/* Tabs */}
      <div className="leaderboard-tabs">
        <button
          className={`tab-btn ${activeTab === 'weekly' ? 'active' : ''}`}
          onClick={() => setActiveTab('weekly')}
        >
          ⭐ {lang.weekly}
        </button>
        <button
          className={`tab-btn ${activeTab === 'alltime' ? 'active' : ''}`}
          onClick={() => setActiveTab('alltime')}
        >
          🏆 {lang.alltime}
        </button>
      </div>

      {/* Your Rank Card */}
      {userRank && userRank.user_rank && (
        <div className="your-rank-banner">
          <div className="rank-highlight">
            <span className="rank-label">{lang.yourRank}</span>
            <span className="rank-value">#{userRank.user_rank}</span>
          </div>
          <div className="rank-detail">
            <span>{userRank.total_xp} XP</span>
          </div>
        </div>
      )}

      {/* Leaderboard List */}
      <div className="leaderboard-content">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
          </div>
        ) : (
          <div className="leaders-list">
            {(activeTab === 'weekly' ? weeklyLeaders : alltimeLeaders).map((leader) => (
              <div 
                key={leader.user_id}
                className={`leaderboard-item ${leader.user_id === user?.id ? 'current-user' : ''}`}
              >
                <div className="rank-medal">
                  {leader.rank <= 3 ? (
                    <span className="medal">{getRankMedal(leader.rank)}</span>
                  ) : (
                    <span className="rank-num">#{leader.rank}</span>
                  )}
                </div>
                
                <div className="user-info">
                  <span className="user-name">
                    {formatDisplayName(leader.display_name)}
                    {leader.user_id === user?.id && (
                      <span className="you-badge">(You)</span>
                    )}
                  </span>
                  <div className="user-stats">
                    <span className="stat">
                      <span className="stat-icon">🔥</span>
                      {leader.streak} days
                    </span>
                  </div>
                </div>

                <div className="user-xp">
                  {leader.total_xp} XP
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
        </>
      )}

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
};

export default LeaderboardScreen;

